/**
 * Формат фильма: фильм, сериал, мультфильм и так далее.
 *
 * Раздел «Фильмы» держит всё, что смотрят, — и полнометражный фильм, и
 * сериал на пять сезонов, и мультфильм с детьми. Жанр на это не отвечает:
 * «Драма» бывает и фильмом, и сериалом. Формат — отдельное поле, видно его в
 * карточке и по нему можно отобрать.
 *
 * Справочник лежит в `shared`, а не в разделе: по нему заполняет поле и
 * миграция базы, и восстановление старой копии, а слой базы разделы не видит.
 */

import type { MediaFormat } from '@/shared/db/schema';

export const MEDIA_FORMATS: MediaFormat[] = [
  'film',
  'series',
  'dorama',
  'cartoon',
  'animatedSeries',
  'anime',
  'documentary',
  'show',
];

export const MEDIA_FORMAT_LABELS: Record<MediaFormat, string> = {
  film: 'Фильм',
  series: 'Сериал',
  dorama: 'Дорама',
  cartoon: 'Мультфильм',
  animatedSeries: 'Мультсериал',
  anime: 'Аниме',
  documentary: 'Документальный',
  show: 'Шоу',
};

/*
  Формат складывается из двух вопросов: что это — игровое, анимация, аниме,
  документальное, шоу — и одна ли это история или сериал. Поэтому «сериал» с
  жанром «Мультфильм» — мультсериал, а сериал с жанром «документальный фильм» —
  документальный.

  Английские слова — для жанров, у которых в Wikidata нет русского названия.
*/
const ANIME = /аниме|anime/;
const ANIMATION = /мультфильм|мультсериал|мультипликац|анимац|animated|animation/;
const DOCUMENTARY = /документальн|documentary/;
const SHOW = /шоу|телепередач|реалити|talk show|reality/;
/** Дорама — азиатский сериал; аниме, мультсериал и шоу проверяются раньше. */
const DORAMA = /дорам|k-drama|j-drama|c-drama/;
const SERIAL = /сериал|ситком|series|sitcom/;

/**
 * Формат по словам каталога: типу записи и жанрам. Так он заполняется при
 * добавлении из каталога и у того, что лежало в базе до появления поля.
 * Без подсказок — фильм: так в каталогах почти всё. Человек поправит в
 * карточке, если ошибся каталог.
 */
export function guessMediaFormat(words: readonly string[]): MediaFormat {
  const text = words.join(' ').toLowerCase();

  if (ANIME.test(text)) return 'anime';
  if (ANIMATION.test(text)) return SERIAL.test(text) ? 'animatedSeries' : 'cartoon';
  if (DOCUMENTARY.test(text)) return 'documentary';
  if (SHOW.test(text)) return 'show';
  if (DORAMA.test(text)) return 'dorama';
  return SERIAL.test(text) ? 'series' : 'film';
}
